import type { ReactNode } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { type FieldErrors, useFormContext } from "react-hook-form";

function getNestedError(
  errors: FieldErrors | undefined,
  name: string | undefined,
): { message?: unknown } | undefined {
  if (!errors || !name) return undefined;
  return name
    .split(".")
    .reduce<unknown>(
      (acc, segment) =>
        acc == null
          ? acc
          : (acc as Record<string, unknown>)[segment],
      errors,
    ) as { message?: unknown } | undefined;
}

type Props = {
  name: string;
  label?: string;
  helperText?: string;
  tooltip?: string;
  required?: boolean;
  hideLabel?: boolean;
  children: ReactNode;
};

function FieldGroup({
  name,
  label,
  helperText,
  tooltip,
  required,
  hideLabel,
  children,
}: Props) {
  const formContext = useFormContext();
  const errorObject = getNestedError(formContext?.formState?.errors, name);
  const errorMessage = errorObject?.message;

  return (
    <Box sx={{ mb: 2 }}>
      {!hideLabel ? (
        <Tooltip title={tooltip ?? ""} arrow placement="top" disableHoverListener={!tooltip}>
          <Box
            component="label"
            htmlFor={name}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              display: "inline-block",
              mb: 0.75,
              color: "text.primary",
            }}
          >
            {label}
            {required ? (
              <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                *
              </Box>
            ) : null}
          </Box>
        </Tooltip>
      ) : null}
      {children}
      {errorMessage ? (
        <Typography
          variant="caption"
          sx={{ color: "error.main", display: "block", mt: 0.5 }}
        >
          {String(errorMessage)}
        </Typography>
      ) : null}
      {!errorMessage && helperText ? (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

export default FieldGroup;
